import {
    Box,
    Button,
    CircularProgress,
    Skeleton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import {
    Building2,
    Check,
    ChevronDown,
    Eye,
    MapPin,
    Phone,
    User,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import * as Yup from 'yup';
import { formatCNPJInput, isValidCNPJ } from '../../common/formatters';
import { httpClient } from '../../common/httpclient';
import Dropdown from '../../component/dropdown/Dropdown';
import InputMasked from '../../component/input/InputMasked';
import { sidebarExpandedSelector } from '../../component/layout/sidebarState';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import MobileCustomerForm from '../../component/customer/MobileCustomerForm';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';

// Lista estática de tipos de atividade (PJ) — mesmas opções do formulário
// anterior, inalteradas.
const ACTIVITY_TYPES = [
    'Industrial',
    'Comercial (Varejista)',
    'Prestação de Serviço',
    'Construção Civil',
    'Comercial (Atacadista)',
    'Comercial (Exportadora)',
    'Importador (Varejista)',
    'Importador (Atacadista)',
    'Associação',
].map((label) => ({ id: label, name: label }));

/**
 * Seção colapsável do formulário — copiada 1:1 de `products/Form.js`
 * (DESIGN-SYSTEM.md §5.5 "Accordion insertion form" / §6.2).
 */
function AccordionSection({ open, complete, icon: Icon, title, subtitle, onToggle, children }) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                border: '1.5px solid',
                borderColor: open ? 'primary.main' : 'divider',
                borderRadius: `${theme.customRadius.hero}px`,
                overflow: 'hidden',
                backgroundColor: 'background.paper',
                boxShadow: open ? `0 0 0 3px ${theme.palette.info.bg}` : 'none',
                transition: 'border-color .2s, box-shadow .2s',
            }}
        >
            <Box
                onClick={onToggle}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: '18px 20px', cursor: 'pointer' }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundImage: theme.palette.gradient.brand,
                        color: '#fff',
                    }}
                >
                    <Icon size={19} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{title}</Typography>
                    <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
                </Box>
                {complete && (
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            backgroundColor: 'success.main',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Check size={12} strokeWidth={3} />
                    </Box>
                )}
                <ChevronDown
                    size={16}
                    color={theme.palette.text.disabled}
                    style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
                />
            </Box>
            <Box sx={{ maxHeight: open ? 2000 : 0, overflow: 'hidden', transition: 'max-height .35s ease' }}>
                <Box sx={{ p: '16px 20px 24px' }}>{children}</Box>
            </Box>
        </Box>
    );
}

function PreviewRow({ label, value, highlight }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
            <span>{label}</span>
            <Box component="b" sx={{ color: highlight ? 'success.main' : 'text.primary', textAlign: 'right', wordBreak: 'break-word' }}>
                {value || '—'}
            </Box>
        </Box>
    );
}

export default function Form() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const isMobileShell = useIsMobileShell();
    useAppBarTitle(isMobileShell && id ? 'Editar cliente' : '');
    // Largura real do menu lateral — usada para alinhar o rodapé de ações fixo
    // (`.action-footer` do protótipo) com a borda do conteúdo. Ref: `products/Form.js`.
    const isSidebarMobile = useMediaQuery('(max-width:1023.95px)');
    const isSidebarExpanded = useRecoilValue(sidebarExpandedSelector);
    const actionFooterLeft = isSidebarMobile ? 0 : (isSidebarExpanded ? 264 : 76);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [loadingCep, setLoadingCep] = useState(false);
    const [customer, setCustomer] = useState(null);
    // Tipo já cadastrado (só existe em edição) — usado para restringir a
    // troca de aba PF/PJ ao tipo original do cliente.
    const [customerType, setCustomerType] = useState(null);
    // Aba ativa do seg-tabs PF/PJ.
    const [personType, setPersonType] = useState('pf');
    // Seções do acordeão abertas de forma independente (não exclusiva): cada
    // seção guarda seu próprio estado de expansão, permitindo múltiplas
    // seções abertas ao mesmo tempo.
    const [openSections, setOpenSections] = useState(['main']);
    const isSectionOpen = (key) => openSections.includes(key);
    const toggleSection = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
    const ensureSectionOpen = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev : [...prev, key]
    ));

    const goBack = () => navigate('/customers');

    const pfValidationSchema = Yup.object({
        first_name: Yup.string().required('Nome é obrigatório'),
        last_name: Yup.string().required('Sobrenome é obrigatório'),
        email: Yup.string().email('E-mail inválido'),
        cell_phone: Yup.string(),
        phone_number: Yup.string(),
        address: Yup.string(),
        complement: Yup.string(),
        number_address: Yup.number(),
        neighborhood: Yup.string(),
        city: Yup.string(),
        cep: Yup.string()
            .matches(/^\d{5}-\d{3}$/, 'CEP inválido')
    });

    const pjValidationSchema = Yup.object({
        trade_name: Yup.string().required('Nome fantasia é obrigatório'),
        legal_name: Yup.string().required('Razão social é obrigatória'),
        cnpj: Yup.string()
            .required('CNPJ é obrigatório')
            // `isValidCNPJ` já cobre formato (12 primeiros caracteres letra
            // OU dígito + 2 dígitos verificadores — CNPJ alfanumérico, Nota
            // Técnica 2026.001 da Receita Federal) e o cálculo do DV numa
            // única checagem — evita manter uma regex de formato solta e
            // desalinhada do validador real.
            .test('cnpj-dv', 'CNPJ inválido', (value) => !value || isValidCNPJ(value)),
        state_registration: Yup.string().required('Inscrição estadual é obrigatória'),
        municipal_registration: Yup.string().required('Inscrição municipal é obrigatória'),
        activity_type: Yup.string().required('Tipo de atividade é obrigatório'),
        principal_cnae: Yup.string(),
        email: Yup.string().email('E-mail inválido'),
        cell_phone: Yup.string(),
        phone_number: Yup.string(),
        address: Yup.string(),
        complement: Yup.string(),
        number_address: Yup.number(),
        neighborhood: Yup.string(),
        city: Yup.string(),
        cep: Yup.string()
            .matches(/^\d{5}-\d{3}$/, 'CEP inválido')
    });

    const handleShowSuccessSnackbar = () => {
        setOpenSnackbar(true);
        setMessage(id ? 'Cliente atualizado com sucesso!' : 'Cliente cadastrado com sucesso!');
        setSeverity('success');
    };

    const handleShowErrorSnackbar = (responseData) => {
        setOpenSnackbar(true);
        let text = 'Erro desconhecido';
        if (typeof responseData === 'string') {
            text = responseData;
        } else if (responseData && typeof responseData === 'object') {
            text = responseData.message || responseData.error || responseData.id || JSON.stringify(responseData);
        }
        setMessage(text);
        setSeverity('error');
    };

    const pfFormik = useFormik({
        initialValues: {
            first_name: '',
            last_name: '',
            email: '',
            cpf: '',
            birthdate: '',
            cell_phone: '',
            phone_number: '',
            address: '',
            complement: '',
            number_address: '',
            neighborhood: '',
            city: '',
            cep: ''
        },
        validationSchema: pfValidationSchema,
        onSubmit: (values) => {
            if (id) {
                handleUpdateCustomer(values);
            } else {
                handleCreateCustomer(values);
            }
        },
    });

    const pjFormik = useFormik({
        initialValues: {
            trade_name: '',
            legal_name: '',
            cnpj: '',
            state_registration: '',
            municipal_registration: '',
            activity_type: '',
            principal_cnae: '',
            email: '',
            cell_phone: '',
            phone_number: '',
            address: '',
            complement: '',
            number_address: '',
            neighborhood: '',
            city: '',
            cep: ''
        },
        validationSchema: pjValidationSchema,
        onSubmit: (values) => {
            if (id) {
                handleUpdateCustomer(values);
            } else {
                handleCreateCustomer(values);
            }
        },
    });

    // Máscara manual do CNPJ (aceita letra OU dígito nos 12 primeiros
    // caracteres — CNPJ alfanumérico, Nota Técnica 2026.001 da Receita
    // Federal). Mesma técnica de `handleLicensePlateChange`
    // (`pages/vehicles/Form.js`), o outro campo do app que mistura letra e
    // número.
    const handleCnpjChange = (event) => {
        pjFormik.setFieldValue('cnpj', formatCNPJInput(event.target.value));
    };

    const handleCreateCustomer = async (values) => {
        try {
            setLoading(true);
            const endpoint = `/customers/${personType === 'pf' ? 'person' : 'enterprise'}`;

            const customerData = personType === 'pf' ? {
                firstName: values.first_name,
                lastName: values.last_name,
                email: values.email,
                cpf: values.cpf,
                birthDate: values.birthdate,
                cellPhone: values.cell_phone,
                phoneNumber: values.phone_number,
                address: values.address,
                complement: values.complement,
                numberAddress: parseInt(values.number_address),
                neighborhood: values.neighborhood,
                city: values.city,
                cep: values.cep,
                customer_type: 'pf'
            } : {
                tradeName: values.trade_name,
                legalName: values.legal_name,
                cnpj: values.cnpj,
                stateRegistration: values.state_registration,
                municipalRegistration: values.municipal_registration,
                activityType: values.activity_type,
                principalCnae: values.principal_cnae,
                email: values.email,
                cellPhone: values.cell_phone,
                phone: values.phone_number,
                address: values.address,
                complement: values.complement,
                numberAddress: parseInt(values.number_address),
                neighborhood: values.neighborhood,
                city: values.city,
                cep: values.cep,
                customer_type: 'pj'
            };

            const response = await httpClient.post(endpoint, customerData);
            if (response.status !== 201) {
                handleShowErrorSnackbar(response.data);
                return;
            }
            personType === 'pf' ? pfFormik.resetForm() : pjFormik.resetForm();
            handleShowSuccessSnackbar();
        } catch (error) {
            console.error('Error creating customer:', error);
            handleShowErrorSnackbar(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateCustomer = async (values) => {
        try {
            setLoading(true);
            const endpoint = `/customers/${personType === 'pf' ? 'person' : 'enterprise'}/${id}`;

            const customerData = personType === 'pf' ? {
                first_name: values.first_name,
                last_name: values.last_name,
                email: values.email,
                cpf: values.cpf,
                birthdate: values.birthdate,
                cell_phone: values.cell_phone,
                phone_number: values.phone_number,
                address: values.address,
                complement: values.complement,
                number_address: parseInt(values.number_address),
                neighborhood: values.neighborhood,
                city: values.city,
                cep: values.cep,
                customer_type: 'pf'
            } : {
                trade_name: values.trade_name,
                legal_name: values.legal_name,
                cnpj: values.cnpj,
                state_registration: values.state_registration,
                municipal_registration: values.municipal_registration,
                activity_type: values.activity_type,
                principal_cnae: values.principal_cnae,
                email: values.email,
                cell_phone: values.cell_phone,
                phone_number: values.phone_number,
                address: values.address,
                complement: values.complement,
                number_address: parseInt(values.number_address),
                neighborhood: values.neighborhood,
                city: values.city,
                cep: values.cep,
                customer_type: 'pj'
            };

            const response = await httpClient.put(endpoint, customerData);
            if (response.status === 204) {
                handleShowSuccessSnackbar();
            } else {
                handleShowErrorSnackbar(response.data);
            }
        } catch (error) {
            console.error('Error updating customer:', error);
            handleShowErrorSnackbar(error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomer = async () => {
        try {
            setLoadingData(true);
            const response = await httpClient.get(`/customers/${id}`);
            if (response.status === 200) {
                const customerData = response.data;
                setCustomer(customerData);

                if (customerData.customer_type) {
                    setCustomerType(customerData.customer_type);
                } else if (customerData.cpf || customerData.name || customerData.lastName) {
                    setCustomerType('pf');
                } else if (customerData.cnpj || customerData.tradeName || customerData.legalName) {
                    setCustomerType('pj');
                }
            }
        } catch (error) {
            console.error('Error fetching customer:', error);
            handleShowErrorSnackbar('Erro ao carregar dados do cliente');
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchCustomer();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Define a aba correta baseada no tipo de cliente ao carregar em edição.
    useEffect(() => {
        if (id && customerType) {
            setPersonType(customerType === 'pj' ? 'pj' : 'pf');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerType, id]);

    // Preenche os formulários quando o cliente é carregado (modo edição).
    useEffect(() => {
        if (!customer || !id) return;

        if (customer.customer_type === 'pf' || customer.first_name || customer.last_name || customer.cpf) {
            pfFormik.setValues({
                first_name: customer.first_name || '',
                last_name: customer.last_name || '',
                email: customer.email || '',
                cpf: customer.cpf || '',
                birthdate: customer.birthdate || customer.birth_date || customer.birthDate || '',
                cell_phone: customer.cell_phone || '',
                phone_number: customer.phone_number || '',
                address: customer.address || '',
                complement: customer.complement || '',
                number_address: customer.number_address || '',
                neighborhood: customer.neighborhood || '',
                city: customer.city || '',
                cep: customer.cep || ''
            });
        } else if (customer.customer_type === 'pj' || customer.trade_name || customer.legal_name || customer.tradeName || customer.legalName || customer.cnpj) {
            pjFormik.setValues({
                trade_name: customer.trade_name || customer.tradeName || '',
                legal_name: customer.legal_name || customer.legalName || '',
                cnpj: customer.cnpj || '',
                state_registration: customer.state_registration || customer.stateRegistration || '',
                municipal_registration: customer.municipal_registration || customer.municipalRegistration || '',
                activity_type: customer.activity_type || customer.activityType || '',
                principal_cnae: customer.principal_cnae || customer.principalCnae || '',
                email: customer.email || '',
                cell_phone: customer.cell_phone || customer.cellPhone || '',
                phone_number: customer.phone_number || customer.phone || '',
                address: customer.address || '',
                complement: customer.complement || '',
                number_address: customer.number_address || customer.numberAddress || '',
                neighborhood: customer.neighborhood || '',
                city: customer.city || '',
                cep: customer.cep || ''
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer, id]);

    const handleGetCep = async (e) => {
        const cep = e.target.value?.trim();
        if (!/^\d{5}-\d{3}$/.test(cep)) return;

        setLoadingCep(true);
        try {
            const response = await httpClient.get(`/cep?cep=${cep}`);
            if (response.status === 200) {
                const data = response.data;
                pjFormik.setValues({ ...pjFormik.values, cep: cep, address: data.logradouro, neighborhood: data.bairro, city: data.localidade });
                pfFormik.setValues({ ...pfFormik.values, cep: cep, address: data.logradouro, neighborhood: data.bairro, city: data.localidade });
            }
        } finally {
            setLoadingCep(false);
        }
    };

    // Restrição de aba PF/PJ ao tipo já cadastrado durante edição — mesma
    // regra do formulário anterior (aba do outro tipo fica oculta).
    const showPfTab = !id || customerType !== 'pj';
    const showPjTab = !id || customerType !== 'pf';

    const activeFormik = personType === 'pf' ? pfFormik : pjFormik;
    const pfRequiredFields = ['first_name', 'last_name'];
    const pjRequiredFields = ['trade_name', 'legal_name', 'cnpj', 'state_registration', 'municipal_registration', 'activity_type'];
    const requiredFields = personType === 'pf' ? pfRequiredFields : pjRequiredFields;
    const isFieldFilled = (key) => Boolean(String(activeFormik.values[key] ?? '').trim());
    // Só a seção "Dados pessoais/da empresa" tem campos obrigatórios — Contato
    // e Endereço são opcionais, igual ao formulário anterior/`products/Form.js`.
    const isMainSectionComplete = requiredFields.every(isFieldFilled);
    const progressPct = isMainSectionComplete ? 100 : 0;
    const RING_R = 23;
    const RING_CIRC = 2 * Math.PI * RING_R;
    const ringOffset = RING_CIRC - (RING_CIRC * progressPct / 100);

    const sections = [
        {
            key: 'main',
            title: personType === 'pf' ? 'Dados pessoais' : 'Dados da empresa',
            subtitle: personType === 'pf' ? 'Nome e documento do cliente' : 'Razão social, CNPJ e inscrições',
            icon: personType === 'pf' ? User : Building2,
            complete: isMainSectionComplete,
        },
        {
            key: 'contact',
            title: 'Contato',
            subtitle: 'E-mail e telefone para comunicação',
            icon: Phone,
            complete: false,
        },
        {
            key: 'address',
            title: 'Endereço',
            subtitle: 'Opcional — usado para entregas',
            icon: MapPin,
            complete: false,
        },
    ];

    const requiredFieldLabel = (text) => (
        <>
            {text} <Box component="span" sx={{ color: 'error.main' }}>*</Box>
        </>
    );

    // Ref: `products/Form.js` `standaloneFieldSx`/`standaloneInputBoxSx` —
    // mesma casca visual de campo "ff-input" (52px, radius 12px, fundo
    // surface-2, label flutuante interno) aplicada aqui verbatim.
    const standaloneInputBoxSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'background.subtle',
            height: '52px',
            transition: 'background-color .15s ease, box-shadow .15s ease',
        },
        '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
            padding: '21px 14px 7px 14px',
        },
        '& .MuiSelect-select.MuiOutlinedInput-input': {
            padding: '0 14px',
            display: 'flex',
            alignItems: 'center',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1.5px',
            borderColor: 'divider',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: '1.5px',
        },
    };

    const standaloneFieldSx = {
        '& .MuiInputBase-input': { fontSize: '13.5px' },
        '& .MuiInputLabel-root': {
            fontSize: '13.5px',
            color: 'text.disabled',
            transform: 'translate(14px, 17px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '10px',
            fontWeight: 800,
            color: 'primary.main',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            transform: 'translate(14px, 8px) scale(1)',
        },
        ...standaloneInputBoxSx,
    };

    // Preview ao vivo — contact-card style (DESIGN-SYSTEM.md §5.5 "sticky
    // live-preview panel ... contact-card style for a customer").
    const getInitials = () => {
        if (personType === 'pf') {
            const f = (pfFormik.values.first_name || '').trim()[0] || '';
            const l = (pfFormik.values.last_name || '').trim()[0] || '';
            return (f + l).toUpperCase() || '?';
        }
        const name = (pjFormik.values.trade_name || pjFormik.values.legal_name || '').trim();
        if (!name) return '?';
        return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
    };

    const previewName = personType === 'pf'
        ? ([pfFormik.values.first_name, pfFormik.values.last_name].filter(Boolean).join(' ') || 'Nome do cliente')
        : (pjFormik.values.trade_name || pjFormik.values.legal_name || 'Nome do cliente');
    const previewSub = personType === 'pf' ? 'Pessoa Física' : 'Pessoa Jurídica';
    const previewDocLabel = personType === 'pf' ? 'CPF' : 'CNPJ';
    const previewDoc = personType === 'pf' ? pfFormik.values.cpf : pjFormik.values.cnpj;
    const previewEmail = activeFormik.values.email;
    const previewPhone = activeFormik.values.cell_phone || activeFormik.values.phone_number;
    const previewCity = activeFormik.values.city;

    if (loadingData) {
        return (
            <PageScaffold
                titlePageTarget="Clientes"
                links={[{ title: 'Clientes', link: '/customers' }]}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={message}
                severity={severity}
            >
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px' }}>
                    <Stack spacing={1.75}>
                        <Skeleton variant="rounded" height={220} sx={{ borderRadius: '18px' }} />
                        <Skeleton variant="rounded" height={180} sx={{ borderRadius: '18px' }} />
                        <Skeleton variant="rounded" height={180} sx={{ borderRadius: '18px' }} />
                    </Stack>
                    <Skeleton variant="rounded" height={420} sx={{ borderRadius: '20px' }} />
                </Box>
            </PageScaffold>
        );
    }

    // Shell mobile: formulário do protótipo `clientes/form.html` (mesmos campos, validação e envio).
    if (isMobileShell) {
        return (
            <PageScaffold
                titlePageTarget="Clientes"
                links={[{ title: 'Clientes', link: '/customers' }]}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={message}
                severity={severity}
            >
                <MobileCustomerForm
                    personType={personType}
                    onPersonTypeChange={setPersonType}
                    showPfTab={showPfTab}
                    showPjTab={showPjTab}
                    pfFormik={pfFormik}
                    pjFormik={pjFormik}
                    isSectionOpen={isSectionOpen}
                    onToggleSection={toggleSection}
                    loading={loading}
                    loadingCep={loadingCep}
                    isEdit={Boolean(id)}
                    activityTypes={ACTIVITY_TYPES}
                    onCnpjChange={handleCnpjChange}
                    onCepBlur={handleGetCep}
                    onCancel={goBack}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            titlePageTarget="Clientes"
            links={[{ title: 'Clientes', link: '/customers' }]}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={message}
            severity={severity}
        >
            <Box>
                {/* form-hero — título + descrição + anel de progresso, igual a `products/Form.js`. */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '20px',
                        flexWrap: 'wrap',
                        mb: 2.25,
                    }}
                >
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                            {id ? 'Editar cliente' : 'Novo cliente'}
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'text.secondary' }}>
                            Preencha por seções — o cartão ao lado mostra o resultado em tempo real.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
                        <Box sx={{ position: 'relative', width: 54, height: 54, flexShrink: 0 }}>
                            <svg viewBox="0 0 54 54" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                                <circle cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" stroke={theme.palette.divider} />
                                <circle
                                    cx="27"
                                    cy="27"
                                    r={RING_R}
                                    fill="none"
                                    strokeWidth="5"
                                    strokeLinecap="round"
                                    stroke={theme.palette.primary.main}
                                    strokeDasharray={RING_CIRC}
                                    strokeDashoffset={ringOffset}
                                    style={{ transition: 'stroke-dashoffset .35s ease' }}
                                />
                            </svg>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 12,
                                    fontWeight: 800,
                                }}
                            >
                                {progressPct}%
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Cadastro</Typography>
                            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                completo conforme você preenche
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* seg-tabs PF/PJ — ToggleButtonGroup (§9), restrito ao tipo já
                    cadastrado durante edição. */}
                <ToggleButtonGroup
                    exclusive
                    fullWidth
                    value={personType}
                    onChange={(e, value) => value && setPersonType(value)}
                    sx={{ mb: 2.25 }}
                >
                    {showPfTab && (
                        <ToggleButton value="pf" sx={{ gap: 1 }}>
                            <User size={14} /> Pessoa Física
                        </ToggleButton>
                    )}
                    {showPjTab && (
                        <ToggleButton
                            value="pj"
                            sx={{
                                gap: 1,
                                '&.Mui-selected, &.Mui-selected:hover': {
                                    color: 'secondary.main',
                                },
                            }}
                        >
                            <Building2 size={14} /> Pessoa Jurídica
                        </ToggleButton>
                    )}
                </ToggleButtonGroup>

                {/* step-pills */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.25 }}>
                    {sections.map((sec, idx) => {
                        const isSecOpen = isSectionOpen(sec.key);
                        return (
                            <Box
                                key={sec.key}
                                onClick={() => ensureSectionOpen(sec.key)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '7px',
                                    px: '14px',
                                    py: '8px',
                                    borderRadius: '24px',
                                    border: '1.5px solid',
                                    borderColor: isSecOpen ? 'primary.main' : 'divider',
                                    backgroundColor: isSecOpen ? 'info.bg' : 'background.paper',
                                    color: isSecOpen ? 'primary.main' : 'text.secondary',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 17,
                                        height: 17,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: sec.complete ? 'success.main' : (isSecOpen ? 'primary.main' : 'text.disabled'),
                                        backgroundColor: sec.complete ? 'success.main' : 'transparent',
                                        color: sec.complete ? '#fff' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 9,
                                        flexShrink: 0,
                                    }}
                                >
                                    {sec.complete ? <Check size={10} strokeWidth={3} /> : idx + 1}
                                </Box>
                                {sec.title}
                            </Box>
                        );
                    })}
                </Box>

                {/* form-columns — acordeão + preview sticky */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px', alignItems: 'start', '& > *': { minWidth: 0 } }}>
                    <Stack spacing={1.75} sx={{ minWidth: 0 }} component="form" onSubmit={activeFormik.handleSubmit}>
                        <AccordionSection
                            open={isSectionOpen('main')}
                            complete={sections[0].complete}
                            icon={sections[0].icon}
                            title={sections[0].title}
                            subtitle={sections[0].subtitle}
                            onToggle={() => toggleSection('main')}
                        >
                            {personType === 'pf' ? (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, '& > *': { minWidth: 0 } }}>
                                    <TextField
                                        autoFocus
                                        label={requiredFieldLabel('Nome')}
                                        fullWidth
                                        name="first_name"
                                        value={pfFormik.values.first_name}
                                        onChange={pfFormik.handleChange}
                                        error={pfFormik.touched.first_name && Boolean(pfFormik.errors.first_name)}
                                        helperText={pfFormik.touched.first_name && pfFormik.errors.first_name}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                    <TextField
                                        label={requiredFieldLabel('Sobrenome')}
                                        fullWidth
                                        name="last_name"
                                        value={pfFormik.values.last_name}
                                        onChange={pfFormik.handleChange}
                                        error={pfFormik.touched.last_name && Boolean(pfFormik.errors.last_name)}
                                        helperText={pfFormik.touched.last_name && pfFormik.errors.last_name}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                    <InputMasked
                                        label="Data de nascimento"
                                        fullWidth
                                        name="birthdate"
                                        mask="99/99/9999"
                                        value={pfFormik.values.birthdate}
                                        onChange={pfFormik.handleChange}
                                        error={pfFormik.touched.birthdate && Boolean(pfFormik.errors.birthdate)}
                                        helperText={pfFormik.touched.birthdate && pfFormik.errors.birthdate}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                    <InputMasked
                                        label="CPF"
                                        fullWidth
                                        name="cpf"
                                        mask="999.999.999-99"
                                        value={pfFormik.values.cpf}
                                        onChange={pfFormik.handleChange}
                                        error={pfFormik.touched.cpf && Boolean(pfFormik.errors.cpf)}
                                        helperText={pfFormik.touched.cpf && pfFormik.errors.cpf}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, '& > *': { minWidth: 0 } }}>
                                    <TextField
                                        autoFocus
                                        label={requiredFieldLabel('Nome Fantasia')}
                                        fullWidth
                                        name="trade_name"
                                        value={pjFormik.values.trade_name}
                                        onChange={pjFormik.handleChange}
                                        error={pjFormik.touched.trade_name && Boolean(pjFormik.errors.trade_name)}
                                        helperText={pjFormik.touched.trade_name && pjFormik.errors.trade_name}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                    <TextField
                                        label={requiredFieldLabel('Razão Social')}
                                        fullWidth
                                        name="legal_name"
                                        value={pjFormik.values.legal_name}
                                        onChange={pjFormik.handleChange}
                                        error={pjFormik.touched.legal_name && Boolean(pjFormik.errors.legal_name)}
                                        helperText={pjFormik.touched.legal_name && pjFormik.errors.legal_name}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                    <TextField
                                        label="CNPJ"
                                        fullWidth
                                        name="cnpj"
                                        value={pjFormik.values.cnpj}
                                        onChange={handleCnpjChange}
                                        error={pjFormik.touched.cnpj && Boolean(pjFormik.errors.cnpj)}
                                        helperText={pjFormik.touched.cnpj && pjFormik.errors.cnpj}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                    <Dropdown
                                        label={requiredFieldLabel('Tipo de Atividade')}
                                        labelVariant="static-micro"
                                        data={ACTIVITY_TYPES}
                                        selectedItem={pjFormik.values.activity_type}
                                        handleChange={(value) => pjFormik.setFieldValue('activity_type', value)}
                                        error={pjFormik.touched.activity_type && Boolean(pjFormik.errors.activity_type)}
                                        helperText={pjFormik.touched.activity_type && pjFormik.errors.activity_type}
                                        variant="outlined"
                                        size="medium"
                                        sx={standaloneFieldSx}
                                    />
                                    <TextField
                                        label={requiredFieldLabel('Inscrição Estadual')}
                                        fullWidth
                                        name="state_registration"
                                        value={pjFormik.values.state_registration}
                                        onChange={pjFormik.handleChange}
                                        error={pjFormik.touched.state_registration && Boolean(pjFormik.errors.state_registration)}
                                        helperText={pjFormik.touched.state_registration && pjFormik.errors.state_registration}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                    <TextField
                                        label={requiredFieldLabel('Inscrição Municipal')}
                                        fullWidth
                                        name="municipal_registration"
                                        value={pjFormik.values.municipal_registration}
                                        onChange={pjFormik.handleChange}
                                        error={pjFormik.touched.municipal_registration && Boolean(pjFormik.errors.municipal_registration)}
                                        helperText={pjFormik.touched.municipal_registration && pjFormik.errors.municipal_registration}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                    <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                        <TextField
                                            label="CNAE Principal"
                                            fullWidth
                                            name="principal_cnae"
                                            value={pjFormik.values.principal_cnae}
                                            onChange={pjFormik.handleChange}
                                            error={pjFormik.touched.principal_cnae && Boolean(pjFormik.errors.principal_cnae)}
                                            helperText={pjFormik.touched.principal_cnae && pjFormik.errors.principal_cnae}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </AccordionSection>

                        <AccordionSection
                            open={isSectionOpen('contact')}
                            complete={sections[1].complete}
                            icon={sections[1].icon}
                            title={sections[1].title}
                            subtitle={sections[1].subtitle}
                            onToggle={() => toggleSection('contact')}
                        >
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, '& > *': { minWidth: 0 } }}>
                                <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                    <TextField
                                        label="E-mail"
                                        fullWidth
                                        name="email"
                                        value={activeFormik.values.email}
                                        onChange={activeFormik.handleChange}
                                        error={activeFormik.touched.email && Boolean(activeFormik.errors.email)}
                                        helperText={activeFormik.touched.email && activeFormik.errors.email}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                </Box>
                                <InputMasked
                                    label="Celular"
                                    fullWidth
                                    name="cell_phone"
                                    mask="(99) 99999-9999"
                                    value={activeFormik.values.cell_phone}
                                    onChange={activeFormik.handleChange}
                                    error={activeFormik.touched.cell_phone && Boolean(activeFormik.errors.cell_phone)}
                                    helperText={activeFormik.touched.cell_phone && activeFormik.errors.cell_phone}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                                <InputMasked
                                    label="Telefone"
                                    fullWidth
                                    name="phone_number"
                                    mask="(99) 9999-9999"
                                    value={activeFormik.values.phone_number}
                                    onChange={activeFormik.handleChange}
                                    error={activeFormik.touched.phone_number && Boolean(activeFormik.errors.phone_number)}
                                    helperText={activeFormik.touched.phone_number && activeFormik.errors.phone_number}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                            </Box>
                        </AccordionSection>

                        <AccordionSection
                            open={isSectionOpen('address')}
                            complete={sections[2].complete}
                            icon={sections[2].icon}
                            title={sections[2].title}
                            subtitle={sections[2].subtitle}
                            onToggle={() => toggleSection('address')}
                        >
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2, '& > *': { minWidth: 0 } }}>
                                <InputMasked
                                    label="CEP"
                                    fullWidth
                                    disabled={loadingCep}
                                    name="cep"
                                    mask="99999-999"
                                    onBlur={handleGetCep}
                                    value={activeFormik.values.cep}
                                    onChange={activeFormik.handleChange}
                                    error={activeFormik.touched.cep && Boolean(activeFormik.errors.cep)}
                                    helperText={activeFormik.touched.cep && activeFormik.errors.cep}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                                <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                    <TextField
                                        label="Endereço"
                                        fullWidth
                                        disabled={loadingCep}
                                        name="address"
                                        value={activeFormik.values.address}
                                        onChange={activeFormik.handleChange}
                                        error={activeFormik.touched.address && Boolean(activeFormik.errors.address)}
                                        helperText={activeFormik.touched.address && activeFormik.errors.address}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                </Box>
                            </Box>
                            {loadingCep && (
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, mt: -1 }}>
                                    <CircularProgress size={13} />
                                    <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
                                        Buscando endereço pelo CEP...
                                    </Typography>
                                </Stack>
                            )}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2, '& > *': { minWidth: 0 } }}>
                                <TextField
                                    label="Número"
                                    fullWidth
                                    name="number_address"
                                    value={activeFormik.values.number_address}
                                    onChange={activeFormik.handleChange}
                                    error={activeFormik.touched.number_address && Boolean(activeFormik.errors.number_address)}
                                    helperText={activeFormik.touched.number_address && activeFormik.errors.number_address}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                                <TextField
                                    label="Complemento"
                                    fullWidth
                                    name="complement"
                                    value={activeFormik.values.complement}
                                    onChange={activeFormik.handleChange}
                                    error={activeFormik.touched.complement && Boolean(activeFormik.errors.complement)}
                                    helperText={activeFormik.touched.complement && activeFormik.errors.complement}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                                <TextField
                                    label="Bairro"
                                    fullWidth
                                    disabled={loadingCep}
                                    name="neighborhood"
                                    value={activeFormik.values.neighborhood}
                                    onChange={activeFormik.handleChange}
                                    error={activeFormik.touched.neighborhood && Boolean(activeFormik.errors.neighborhood)}
                                    helperText={activeFormik.touched.neighborhood && activeFormik.errors.neighborhood}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                            </Box>
                            <TextField
                                label="Cidade"
                                fullWidth
                                disabled={loadingCep}
                                name="city"
                                value={activeFormik.values.city}
                                onChange={activeFormik.handleChange}
                                error={activeFormik.touched.city && Boolean(activeFormik.errors.city)}
                                helperText={activeFormik.touched.city && activeFormik.errors.city}
                                variant="outlined"
                                sx={standaloneFieldSx}
                            />
                        </AccordionSection>
                    </Stack>

                    {/* preview sticky — contact-card style (DESIGN-SYSTEM.md §5.5). */}
                    <Box sx={{ position: { md: 'sticky' }, top: { md: 20 } }}>
                        <Typography
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '10.5px',
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                letterSpacing: '.06em',
                                color: 'text.disabled',
                                mb: 1.25,
                            }}
                        >
                            <Eye size={13} /> Pré-visualização do cliente
                        </Typography>

                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: `${theme.customRadius.hero}px`,
                                backgroundColor: 'background.paper',
                                boxShadow: theme.customShadows.md,
                                overflow: 'hidden',
                                mb: 1.75,
                            }}
                        >
                            <Box sx={{ height: 78, backgroundImage: theme.palette.gradient.brand, position: 'relative' }}>
                                <Box
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: '22px',
                                        backgroundColor: 'background.paper',
                                        border: '4px solid',
                                        borderColor: 'background.paper',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 22,
                                        fontWeight: 800,
                                        color: 'primary.main',
                                        position: 'absolute',
                                        left: 20,
                                        bottom: -32,
                                        boxShadow: theme.customShadows.md,
                                    }}
                                >
                                    {getInitials()}
                                </Box>
                            </Box>
                            <Box sx={{ p: '42px 20px 20px' }}>
                                <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>
                                    {previewName}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.25 }}>
                                    {previewSub}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.5 }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            px: '10px',
                                            py: '5px',
                                            borderRadius: '20px',
                                            backgroundColor: personType === 'pj' ? 'secondary.bg' : 'info.bg',
                                            color: personType === 'pj' ? 'secondary.main' : 'primary.main',
                                        }}
                                    >
                                        {previewSub}
                                    </Box>
                                </Box>
                                <Box sx={{ height: '1px', backgroundColor: 'divider', my: 2 }} />
                                <PreviewRow label={previewDocLabel} value={previewDoc} />
                                <PreviewRow label="E-mail" value={previewEmail} />
                                <PreviewRow label="Telefone" value={previewPhone} />
                                <PreviewRow label="Cidade" value={previewCity} />
                            </Box>
                        </Box>

                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                            Campos com <Box component="span" sx={{ color: 'error.main' }}>*</Box> são obrigatórios.
                        </Typography>
                    </Box>
                </Box>

                {/* Espaçador para compensar o rodapé fixo abaixo. */}
                <Box sx={{ height: 'calc(84px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />

                {/* action-footer fixo — Cancelar/Salvar. */}
                <Box
                    sx={{
                        position: 'fixed',
                        left: actionFooterLeft,
                        right: 0,
                        bottom: 0,
                        zIndex: theme.zIndex.appBar,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        backgroundColor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 -6px 20px rgba(15,23,42,.06)',
                        px: 3,
                        py: 1.75,
                        // safe-area (PWA standalone com viewport-fit=cover); env() = 0 fora de aparelhos com notch.
                        pl: 'calc(24px + env(safe-area-inset-left, 0px))',
                        pr: 'calc(24px + env(safe-area-inset-right, 0px))',
                        pb: 'calc(14px + env(safe-area-inset-bottom, 0px))',
                        transition: 'left .2s ease',
                    }}
                >
                    <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>
                        Campos com * são obrigatórios
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                        <Button variant="outlined" onClick={goBack}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={activeFormik.handleSubmit}
                            disabled={loading}
                        >
                            {loading ? 'Salvando...' : (id ? 'Atualizar cliente' : 'Salvar cliente')}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </PageScaffold>
    );
}
